import React, { useEffect } from 'react';
import { TarotCardData } from '../types/tarot';

interface CardModalProps {
  card: TarotCardData;
  onClose: () => void;
}

export const CardModal: React.FC<CardModalProps> = ({ card, onClose }) => {
  const isReversed = card.orientation === 'reversed';
  const orientationLabel = isReversed ? 'Reversed' : null;

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[70] p-6">
      <div 
        className="absolute inset-0" 
        onClick={onClose}
      />

      <div className="relative bg-gradient-to-b from-slate-800 to-purple-900 rounded-2xl max-w-md w-full max-h-[90vh] mx-4 shadow-2xl border border-purple-400/30">
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute -top-3 -right-3 w-9 h-9 rounded-full bg-white/30 hover:bg-white/50 text-slate-900 text-sm font-semibold flex items-center justify-center shadow-[0_4px_12px_rgba(10,10,30,0.28)] backdrop-blur-sm"
        >
          ×
        </button>

        <div className="p-5 pt-8 overflow-y-auto max-h-[90vh] space-y-4">
          {/* Card Image - Vertical 1.5:1 ratio */}
          <div className="relative w-full aspect-[2/3] rounded-sm overflow-hidden mb-4 mx-auto max-w-xs">
            <img 
              src={card.image}
              alt={card.name}
              className={`w-full h-full object-cover ${isReversed ? 'rotate-180' : ''}`}
            />
          </div>

          {/* Card Title */}
          <div className="flex flex-col items-center gap-1 mb-3">
            <h2 className="crypto-heading text-xl text-center text-white">
              {card.name}
            </h2>
            {orientationLabel && (
              <span className="text-purple-300/80 text-[10px] uppercase tracking-[0.4em]">
                {orientationLabel}
              </span>
            )}
          </div>

          {/* Card Meaning */}
          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-purple-400/20 max-h-48 overflow-y-auto">
            <h3 className="text-purple-300 font-semibold mb-2 text-center tracking-wide">Insight</h3>
            <p className="text-white/90 text-sm leading-relaxed text-center font-medium whitespace-pre-line">
              {card.cryptoInsight ?? card.meaning}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
